import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@/components/common/button';
import Modal from '@/components/common/modal';
import Typography from '@/components/common/typography';
import { WaterModalContent } from '@/models/water';
import { RootState } from '@/redux/reducers';
import { closeWaterInfoModal } from '@/redux/slices/waterInfo';

import WaterModalTab from './waterModalTab';

const WaterInfoModal: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedState, selectedLayerId, isWaterInfoModalOpen } = useSelector(
    (state: RootState) => state.waterInfo
  );
  const [selectedTab, setSelectedTab] = useState<WaterModalContent | null>(
    null
  );

  const close = useCallback(() => {
    dispatch(closeWaterInfoModal());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedState || !selectedLayerId) return;

    const { layers, modalTabs } = selectedState;

    /**
     * Check to see if the selected state has any Modal Tabs.
     * Modal Tabs will appear instead of layers
     */
    if (modalTabs) {
      const [firstTab] = modalTabs;

      if (firstTab) {
        setSelectedTab(firstTab);
      }
    } else {
      const foundLayer = layers.find(
        (layer) => layer.layer.id === selectedLayerId
      );

      if (foundLayer) {
        setSelectedTab(foundLayer);
      } else {
        if (layers.length === 0) {
          close();
        }
        setSelectedTab(selectedState.layers[0]);
      }
    }
  }, [close, selectedLayerId, selectedState]);

  const renderTabs = () => {
    if (!selectedState) return null;

    const { layers, modalTabs } = selectedState;

    const tabs = modalTabs ?? layers;

    if (tabs) {
      return (
        <div>
          {tabs.map((tab) => (
            <WaterModalTab
              key={tab.title}
              activeTabTitle={selectedTab?.title}
              setActiveTab={setSelectedTab}
              tab={tab}
            />
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      isShifted
      className="absolute z-80"
      content={
        <div className="flex h-[300px] w-[550px] py-6 pl-3 pr-6 text-gray-100">
          {/* Side bar */}

          {renderTabs()}

          {/* Right side information section */}
          {selectedTab && (
            <div className="w-[400px] pl-3">
              {/* Title part */}
              <div className="flex items-center pb-4">
                <Typography className="flex-grow text-gray-100" variant="h3">
                  {selectedTab.title}
                </Typography>

                {/* Close button */}
                <Button
                  className="-mr-2 -mt-2 flex items-center"
                  variant="icon"
                  onClick={close}
                >
                  <span
                    key="close"
                    className="material-icons-outlined text-gray-100"
                  >
                    close
                  </span>
                </Button>
              </div>

              {/* Description */}
              <div>
                <Typography className="text-gray-100" variant="subtitle">
                  {selectedTab.moreInfo.description}
                </Typography>
              </div>
            </div>
          )}
        </div>
      }
      handleOnClose={close}
      isOpen={isWaterInfoModalOpen}
    />
  );
};

export default memo(WaterInfoModal);
